import { VehicleBasic } from '@oneaudi/vtp-shared';
import { TrackingDataV2, TrackingServiceV2 } from '@oneaudi/audi-tracking-service';

interface ProductData {
  productInfo: {
    productId: string;
    productName?: string;
    manufacturer: string;
  };
  category: {
    primaryCategory: string;
    subCategory1: string;
    productType: string;
  };
  attributes: VehicleBasic;
}
export const getRelatedProduct = (vehicle: VehicleBasic): ProductData => {
  return {
    productInfo: {
      productId: vehicle.id || '',
      productName: vehicle.modelCode ? vehicle.modelCode.description : '',
      manufacturer: 'Audi',
    },
    category: {
      primaryCategory: vehicle.symbolicCarlineGroup ? vehicle.symbolicCarlineGroup.code : '',
      subCategory1: vehicle.symbolicCarline ? vehicle.symbolicCarline.code : '',
      productType: vehicle.type === 'N' ? 'new car' : 'used car',
    },
    attributes: vehicle,
  };
};

export const trackFeatureAppReady = (trackingService: TrackingServiceV2, vehicle: VehicleBasic) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: 'vtp details',
        },
        attributes: {
          componentName: 'vtp-carInfo',
          label: '',
          targetURL: '',
          clickID: '',
          elementName: '',
          value: '',
          pos: '',
        },
      },
      componentUpdate: {
        attributes: {
          implementer: 2,
        },
        products: {
          buyableVehicle: [
            {
              productInfo: {
                manufacturer: 'Audi',
                productId: vehicle.id,
                productName: vehicle.modelCode ? vehicle.modelCode.description : '',
              },
              category: {
                primaryCategory: vehicle.symbolicCarlineGroup
                  ? vehicle.symbolicCarlineGroup.code
                  : '',
                productType: vehicle.type === 'N' ? 'new car' : 'used car',
                subCategory1: vehicle.symbolicCarline ? vehicle.symbolicCarline.code : '',
              },
              attributes: vehicle,
            },
          ],
        },
      },
    });
  }
};

export const trackOpenGallery = (
  trackingService: TrackingServiceV2,
  label: string | undefined,
  clickSource: 'button' | 'image' | 'other' | 'icon'
) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'content',
          eventName: 'vtp stage - gallery layer open',
        },
        attributes: {
          componentName: 'vtp-stage',
          label: label || '',
          targetURL: '',
          clickID: '',
          // should be "image": if user clicks on an image to open the gallery, "button" if the click was on a button to open the gallery OR "other" in all other cases
          elementName: clickSource,
          value: ``,
          pos: '',
        },
      },
    });
  }
};

export const trackCloseGallery = (trackingService: TrackingServiceV2) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'content',
          eventName: 'vtp stage - gallery layer close',
        },
        attributes: {
          componentName: 'vtp-stage',
          label: '',
          targetURL: '',
          clickID: '',
          elementName: 'icon',
          value: ``,
          pos: '',
        },
      },
    });
  }
};

export const trackImageChange = (
  trackingService: TrackingServiceV2,
  elementName: 'arrow' | 'image' | 'other',
  position: [number, number],
  actionType: 'swipe' | 'click' | 'automatic'
) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'content',
          eventName: 'vtp stage - change gallery image',
        },
        attributes: {
          componentName: 'vtp-stage',
          label: '',
          targetURL: '',
          clickID: '',
          elementName,
          value: '',
          // We add 1 so that the image tracking is starting from 1, not 0.
          pos: `${position[0] + 1}~${position[1] + 1}`,
          actionType,
        },
      },
    });
  }
};

export const trackThumbnailToggle = (
  trackingService: TrackingServiceV2,
  thumbnailsAreOpening: boolean
) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'content',
          eventName: `vtp stage - thumbnail ${thumbnailsAreOpening ? 'open' : 'close'}`,
        },
        attributes: {
          componentName: 'vtp-stage',
          label: '',
          targetURL: '',
          clickID: '',
          elementName: 'icon',
          value: '',
          pos: '',
        },
      },
    });
  }
};

export const registerImpressionTracking = (
  trackingService: TrackingServiceV2,
  ref: any,
  impressionEvent: TrackingDataV2
) => {
  if (trackingService && impressionEvent) {
    if (ref !== null) {
      trackingService.registerImpressionTracking(ref, () => impressionEvent);
    }
  }
};
export const trackFavourizeClick = (
  trackingService: TrackingServiceV2,
  label: string,
  eventName: string
) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'favorite',
          eventName,
        },
        attributes: {
          label,
          componentName: 'vtp-details',
          elementName: 'icon',
          value: '',
          targetUrl: '',
        },
      },
    });
  }
};
export const trackShareClick = (trackingService: TrackingServiceV2, label: string) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'content',
          eventName: 'vtp share',
        },
        attributes: {
          label,
          componentName: 'vtp-details',
          elementName: 'icon',
          value: '',
          targetUrl: '',
        },
      },
    });
  }
};

export const trackAudiCodeClick = (
  trackingService: TrackingServiceV2,
  label: string,
  code: string
) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'tooltip',
          eventName: 'vtp audi code',
        },
        attributes: {
          label,
          componentName: 'vtp-details',
          elementName: 'icon',
          value: code,
          targetUrl: '',
        },
      },
    });
  }
};
